<template>
    <v-menu
        v-model="menuOpen"
        ref="menu"
        :attach="$refs.popper"
        :close-on-content-click="false"
        content-class="menu-rounded"
        :disabled="disabled"
        transition="popper-scale"
        max-width="290px">
        <!-- :nudge-top="hideDetails ? 0 : 3"     -->
        <!--tooltip-op-80-->
        <template #activator="{ on, attrs }">
            <v-text-field
                ref="textField"
                v-model="textFieldDate"
                v-bind="attrs"
                v-on="on"
                :aria-label="label ? label : 'date/time picker'"
                :disabled="disabled"
                :error-messages="errorMessages"
                :hide-details="hideDetails"
                :hint="format"
                :label="label"
                :outlined="outlined"
                :placeholder="placeholder"
                :single-line="singleLine"
                class="cursor-text input-appended-floating"
                clearable
                dense
                persistent-hint
                persistent-placeholder>
                <template #prepend v-if="!hideIcon">
                    <v-icon :color="iconColor"
                            :disabled="disabled"
                            tabindex="-1"
                            @click="menuOpen =! menuOpen"
                            >
                        far {{ menuOpen ? 'fa-calendar-check' : 'fa-calendar-day' }}
                    </v-icon>
                </template>
            </v-text-field>

            <portal
                selector="#app">
                <div
                    ref="popper"
                    class="popper-menu-anchor"
                    :class="{'popper-menu-hide-under-navbar': !popupUsedInModal}"
                >
                </div>
            </portal>
        </template>

        <v-date-picker
            v-if="menuShown"
            v-model="datepickerDate"
            :active-picker.sync="activePicker"
            :first-day-of-week="1"
            :locale="language"
            :max="maxISO"
            :min="minISO"
            :picker-date.sync="pickerDateShown"
            aria-label="datepicker"
            color="primary"
            no-title
            show-adjacent-months
            @input="menuOpen = false" />

    </v-menu>
</template>

<!--suppress SillyAssignmentJS -->
<script>
import { defineComponent } from 'vue'
import {
    dateISOToDateUS,
    dateUSToDateISO,
    localizeDate,
    unlocalizeDate,
    validateDatabaseDateFormat,
    validateDateFormat, validateISODayMonth
} from '../../../utils/date';
import popperPositionedVuetifyMenuMixin from '../../mixins/popperPositionedVuetifyMenuMixin';
import { Portal } from '@linusborg/vue-simple-portal';

export default defineComponent({
    name: 'datepicker',
    components: { Portal },
    mixins: [popperPositionedVuetifyMenuMixin],
    props: {
        /** Source of truth, should be in US format (MM/dd/yyyy).
         * New value is emitted from 'input' in US format (MM/dd/yyyy). */
        value: {
            type: String
        },
        min: {
            type: String
            //default: '02/01/1000' // Validator function only works with 4-letter years for now.
            // Vuetify 2 date picker only goes down to 1st of February, year 1000. Not 1st of January xD
        },
        max: {
            type: String
            //default: '11/30/9999' // Validator function only works with 4-letter years for now.
            // Vuetify 2 date picker crashes when displaying dates with the year over 10000,
            // and we want to show adjacent days in the calendar. Showing december 9999 would also show the 1st of jan 10000.
        },

        /** String representing the format in which the date should be shown in the textField,
         * using "MM", "dd" and "yyyy" as placeholders. */
        format: {
            type: String,
            default: Laravel.activeDateformat
        },

        /** IETF BCP 47 language tag in which the day/month names should be shown in the v-datepicker */
        language: {
            type: String,
            default: Laravel.activeLanguage
        },

        label: String,
        placeholder: String,

        hideDetails: {
            type: Boolean,
            default: true
        },

        outlined: {
            type: Boolean,
            default: true
        },
        singleLine: Boolean,
        disabled: Boolean,
        alignRight: Boolean,
        popupUsedInModal: Boolean,
        hideIcon: Boolean
    },
    data() {
        return {
            menuOpen: false,

            textFieldFocused: false,
            buttonMouseDown: false,

            timeoutHandle: null,

            activePicker: 'DATE',
            pickerDateShown: null,

            textFieldErrorDateMin: null,
            textFieldErrorDateMax: null,
            textFieldErrorDateFormat: null,

            /* Valid date that was entered in textField when emit is not possible (entered date is outside min/max limits) */
            textFieldEnteredDate: null,

            t_date_format_mismatch: Lang.langGet('Required format :format', { format: this.format }),
            t_date_before_min: Lang.langGet('Date before minimum limit'),
            t_date_after_max: Lang.langGet('Date after maximum limit')
        }
    },
    computed: {
        iconColor() {
            return this.menuOpen ? 'primary' : ''
        },
        valueValidated() {
            if(!this.value) {
                return null;
            }

            try {
                validateDatabaseDateFormat(this.value);
                return this.value;
            } catch (e) {
                return null;
            }
        },
        /* 'value' in ISO date format */
        valueISO() {
            return dateUSToDateISO(this.valueValidated)
        },
        /* 'min' in ISO date format */
        minISO() {
            return dateUSToDateISO(this.min)
        },
        /* 'max' in ISO date format */
        maxISO() {
            return dateUSToDateISO(this.max)
        },
        /* 'value' as JS date object */
        valueDateJs() {
            return this.valueValidated ? new Date(this.valueISO) : null
        },
        /* 'min' as JS date object */
        minDateJs() {
            return this.minISO ? new Date(this.minISO) : null
        },
        /* 'max' as JS date object */
        maxDateJs() {
            return this.maxISO ? new Date(this.maxISO) : null
        },

        errorMessages() {
            const messages = []
            if (this.textFieldErrorDateFormat) messages.push(this.t_date_format_mismatch)
            if (this.textFieldErrorDateMin) messages.push(this.t_date_before_min)
            if (this.textFieldErrorDateMax) messages.push(this.t_date_after_max)
            return messages
        },

        /* v-datepicker accepts and returns date in the ISO 8601 format (YYYY-MM-DD) */
        datepickerDate: {
            get() {
                return this.valueISO
            },
            set(newDate) {
                this.emitInput(dateISOToDateUS(newDate))
            }
        },

        textFieldDate: {
            get() {
                return localizeDate(this.valueValidated, this.format)
            },
            set(newDate) {
                if (!newDate) {
                    this.emitInput(null)
                    return
                }

                this.textFieldErrorDateFormat = !validateDateFormat(newDate, this.format)
                if (this.textFieldErrorDateFormat) {
                    return
                }

                const newDateUS = unlocalizeDate(newDate, this.format)
                const newDateISO = dateUSToDateISO(newDateUS)

                if (!validateISODayMonth(newDateISO)) {
                    this.textFieldErrorDateFormat = true
                    return
                }

                const newDateJs = new Date(newDateISO)

                /* https://masteringjs.io/tutorials/fundamentals/check-if-date-is-valid */
                const isDateValid = !Number.isNaN(newDateJs.valueOf())

                if (!isDateValid) {
                    this.textFieldErrorDateFormat = true
                    return
                }

                let dateLimitsValid = true

                /* Both of these checks must run, do not put them in an if statement and OR them */
                if (this.checkIfDateBeforeMin(newDateJs)) dateLimitsValid = false
                if (this.checkIfDateAfterMax(newDateJs)) dateLimitsValid = false

                /* Save the entered (valid) date, so it can be emitted later if min/max limits change to allow it */
                if (!dateLimitsValid) {
                    this.textFieldEnteredDate = newDate
                    return
                }

                this.emitInput(newDateUS)
            }
        },
        popperOffset() {
            return [31, 0];
        }
    },
    methods: {
        resetPickerDateShown() {
            this.pickerDateShown = (this.datepickerDate ? this.datepickerDate : this.maxISO ? this.maxISO : (new Date()).toISOString()).substring(0, 10)
        },
        checkIfDateBeforeMin(dateJs) {
            this.textFieldErrorDateMin = dateJs && this.minDateJs && (dateJs < this.minDateJs)
            return this.textFieldErrorDateMin
        },
        checkIfDateAfterMax(dateJs) {
            this.textFieldErrorDateMax = dateJs && this.maxDateJs && (dateJs > this.maxDateJs)
            return this.textFieldErrorDateMax
        },
        emitInput(val) {
            this.$emit('input', val ?? '');

            this.textFieldErrorDateFormat = false
            this.textFieldErrorDateMin = false
            this.textFieldErrorDateMax = false

            this.textFieldEnteredDate = null
        },
        restoreTextFieldEnteredDate() {
            if (this.textFieldEnteredDate) {
                this.textFieldDate = this.textFieldEnteredDate
            }
        },
        getReferenceEl() {
            return this.$refs.textField.$el;
        },
        getPopperEl() {
            return this.$refs.popper;
        },
        getVMenuEl() {
            return this.$refs.menu.$el;
        }
    },
    watch: {
        errorMessages(messages) {
            const valid = messages.length === 0;
            this.$emit('valid', valid);
        },
        menuOpen(val) {
            if (val) {
                this.$refs.textField.focus()
                clearTimeout(this.timeoutHandle)
            } else {
                this.timeoutHandle = setTimeout(() => {
                    this.activePicker = 'DATE'
                    this.resetPickerDateShown()
                }, 2500)
            }
        },
        max(val) {
            this.resetPickerDateShown()
        },
        valueValidated() {
            this.checkIfDateBeforeMin(this.valueDateJs)
            this.checkIfDateAfterMax(this.valueDateJs)
            this.resetPickerDateShown()
        },
        minDateJs() {
            this.checkIfDateBeforeMin(this.valueDateJs)
            this.restoreTextFieldEnteredDate()
        },
        maxDateJs() {
            this.checkIfDateAfterMax(this.valueDateJs)
            this.restoreTextFieldEnteredDate()
        },
        textFieldFocused(val) {
            this.$emit(val ? 'focus' : 'blur')
        }
    },
    beforeDestroy() {
        clearTimeout(this.timeoutHandle)
    }
})
</script>
